import { Injectable } from "@angular/core";
import { HttpHeaders, HttpClient } from "@angular/common/http";

import { User, FacebookPage } from "../data/user";
import { Customer, ListingCartItem } from "shopping/cart";

export interface UserListenerInterface {
  permissionsLoaded();
}

@Injectable()
export class UserService {
  httpHeaders = new HttpHeaders({ "Content-Type": "application/json" });
  usersUrl = "api/v2/users";
  public isAdmin: boolean = false;
  public isNowInStore: boolean = false;
  public isEtsy: boolean = false;
  public isTradegecko: boolean = false;
  public isEcwid: boolean = false;
  public isLightspeed: boolean = false;
  public isEbay: boolean = false;
  public isWooCommerce: boolean = false;
  public isPrestashop: boolean = false;
  public isMagento: boolean = false;
  public isManager: boolean = false;
  public isTrial: boolean = true;
  public permissionsLoaded: boolean = false;
  public settings: any;

  userListeners: Array<UserListenerInterface> =
    new Array<UserListenerInterface>();

  constructor(private httpClient: HttpClient) {
    this.me().then((user) => {
      if (user && user.roles) {
        this.isAdmin = user.roles.indexOf("admin") > -1;
        this.isManager = user.roles.indexOf("manager") > -1;
        this.isNowInStore = user.accounts[0].type === "nowinstore";
        this.isEtsy = user.accounts[0].type === "etsy";
        this.isTradegecko = user.accounts[0].type === "tradegecko";
        this.isEbay = user.accounts[0].type === "ebay";
        this.isEcwid = user.accounts[0].type === "ecwid";
        this.isLightspeed = user.accounts[0].type === "lightspeed";
        this.isWooCommerce = user.accounts[0].type === "woocommerce";
        this.isPrestashop = user.accounts[0].type === "prestashop";
        this.isMagento = user.accounts[0].type === "magento";
        this.isTrial = user.subscriptions[0].is_trial;
        this.settings = user.settings;
      }
      this.permissionsLoaded = true;
      this.notifyPermissionsLoaded();
    });
  }

  locale(): String {
    return (this.settings ? this.settings.locale : null) || navigator.language;
  }
  addUserListener(userListener: UserListenerInterface) {
    this.userListeners.push(userListener);
  }

  notifyPermissionsLoaded() {
    this.userListeners.forEach((userListener) => {
      userListener.permissionsLoaded();
    });
  }

  authenticate(email, password) {
    return this.httpClient
      .post(JSON.stringify({ email: email, password: password }), {
        headers: this.httpHeaders,
      })
      .toPromise()
      .then((res) => res as User)
      .catch(this.handleError);
  }

  me(): Promise<User> {
    const url = `${this.usersUrl}/me`;
    return this.httpClient
      .get(url)
      .toPromise()
      .then((response) => response as User)
      .catch(this.handleError);
  }

  refreshToken(): Promise<User> {
    const url = `/auth/google-analytics/refresh-token`;
    return this.httpClient
      .get(url)
      .toPromise()
      .then((response) => response as User)
      .catch(this.handleError);
  }

  isSessionActive(): Promise<boolean> {
    // Chain off me() directly -- wrapping it in a new Promise and swallowing
    // the failure in .catch() left the promise pending forever, which hangs
    // any route guard awaiting it (blank page, no redirect).
    return this.me().then((user) => user.email != null);
  }

  isShopify(user: User): boolean {
    return user.accounts[0].type === "shopify";
  }

  isBigcommerce(user: User): boolean {
    return user.accounts[0].type === "bigcommerce";
  }

  hasExpired(userId?: String): Promise<boolean> {
    if (userId) {
      const url = `${this.usersUrl}/${userId}/has-expired`;
      return this.httpClient
        .get(url)
        .toPromise()
        .then((response) => {
          return (<any>response).has_expired;
        })
        .catch(this.handleError);
    }
    return this.me().then((user) => user.has_expired);
  }

  isRemoteSubscriptionActive(): Promise<boolean> {
    const url = `${this.usersUrl}/is-remote-subscription-active`;
    return this.httpClient
      .get(url)
      .toPromise()
      .then((response) => {
        return (<any>response).active;
      })
      .catch(this.handleError);
  }
  hasBusinessInformation(): Promise<boolean> {
    return this.me().then((user) => user.business_information != null);
  }

  hasAccess(product: string): Promise<boolean> {
    return this.me().then((user) => {
      if (product === "gif-maker") {
        return user.gif_maker_account != null;
      } else if (product === "catalog-builder") {
        return true;
      } else if (product === "tear-sheet") {
        return this.isShopify(user) || this.isBigcommerce(user);
        //TODO
        // && user.subscriptions[0].duration > 60
      } else if (product === "order-sheet") {
        return true;
      } else if (product === "barcode-bulk-order") {
        return this.isShopify(user) || this.isBigcommerce(user);
      } else if (product === "barcode-generation") {
        return this.isShopify(user) || this.isBigcommerce(user);
      } else if (product === "product-categories") {
        return (
          this.isShopify(user) ||
          this.isBigcommerce(user) ||
          this.isEtsy ||
          this.isEcwid ||
          this.isLightspeed ||
          this.isWooCommerce ||
          this.isPrestashop ||
          this.isMagento ||
          this.isNowInStore
        );
      } else if (product === "custom-template") {
        return (
          this.isShopify(user) ||
          this.isBigcommerce(user) ||
          this.isEcwid ||
          this.isLightspeed ||
          this.isPrestashop ||
          this.isMagento
        );
      } else {
        throw "Product doesn't exist";
      }
    });
  }

  facebookPages(): Promise<FacebookPage[]> {
    const url = `${this.usersUrl}/facebook-pages`;
    return this.httpClient
      .get(url)
      .toPromise()
      .then((response) => response as FacebookPage[])
      .catch(this.handleError);
  }

  isEmailUnique(currentEmail: string, newEmail: string): Promise<boolean> {
    if (currentEmail === newEmail) {
      return new Promise<boolean>((resolve) => resolve(true));
    }
    const url = `${this.usersUrl}/count/?email=${newEmail}`;
    return this.httpClient
      .get(url, { headers: this.httpHeaders })
      .toPromise()
      .then((res) => {
        let count = res;
        return count === 0;
      })
      .catch(this.handleError);
  }

  update(user: User): Promise<User> {
    const url = `${this.usersUrl}/me`;
    return this.httpClient
      .put(url, JSON.stringify(user), { headers: this.httpHeaders })
      .toPromise()
      .then((user) => user)
      .catch(this.handleError);
  }

  updateContactEmail(email: String): Promise<any> {
    const url = `${this.usersUrl}/me/contact_email`;
    return this.httpClient
      .put(url, { contact_email: email }, { headers: this.httpHeaders })
      .toPromise()
      .then((user) => user)
      .catch(this.handleError);
  }

  updateWebhook(type: string, enable: boolean) {
    const url = `${this.usersUrl}/me/webhooks`;
    return this.httpClient
      .put(
        url,
        { type: type, enable: enable ? 1 : 0 },
        { headers: this.httpHeaders }
      )
      .toPromise()
      .then((user) => user)
      .catch(this.handleError);
  }

  webhookCount(type: string): Promise<number> {
    const url = `${this.usersUrl}/me/webhooks/count?type=${type}`;
    return this.httpClient
      .get(url)
      .toPromise()
      .then((res: { count: number }) => {
        let count = res.count;
        return count;
      })
      .catch(this.handleError);
  }

  closeAccount(reason: string) {
    return this.httpClient
      .post("/auth/closeAccount", { reason: reason })
      .toPromise()
      .then((res) => res)
      .catch(this.handleError);
  }

  handleError(error: any): Promise<any> {
    console.error("An error occurred", error);
    return Promise.reject(error.message || error);
  }

  public getCustomer(userId: string, email: string): Promise<Customer[]> {
    const url = `${this.usersUrl}/${userId}/customers?&email=${email}`;
    return this.httpClient
      .get(url)
      .toPromise()
      .then((response) => response as Customer[])
      .catch(this.handleError);
  }

  sendOrder(
    userId: string,
    customer: Customer,
    listingCartItems: ListingCartItem[],
    comments: string
  ): Promise<void> {
    const url = `${this.usersUrl}/${userId}/orders`;
    return this.httpClient
      .post(
        url,
        { customer, listingCartItems, comments },
        { headers: this.httpHeaders }
      )
      .toPromise()
      .then((res) => null)
      .catch(this.handleError);
  }
}
